import { computed } from 'vue';
import { usePermittedMembers } from '@/entities/User';
import { formatCollaboratorNameAndRegistration } from '@/shared/lib';
import type { CaslAction, Subjects } from '@/shared/lib/casl';

export interface CollaboratorPermissionCheck {
  action: CaslAction;
  subject: Subjects;
}

export interface CollaboratorOption {
  id: string | number;
  name: string;
}

/** O elaborador vem de fontes com formas diferentes; a que manda é `userId`, com `id` de reserva. */
const collaboratorUserId = (collaborator: { id?: number; userId?: number }): string | number =>
  collaborator.userId ?? collaborator.id ?? '';

const optionLabel = (collaborator: {
  name?: string | null;
  registrationNumber?: string | null;
}): string =>
  formatCollaboratorNameAndRegistration(collaborator.name, collaborator.registrationNumber, {
    labelWhenNameMissing: 'Nome não disponível',
  });

/**
 * As duas listas de colaborador do modal de filtros, que vivem em espaços de id diferentes.
 *
 * "Elaboração" endereça o colaborador por id de `User`, porque as filas resolvem `owner.userId` e
 * `creator.userId`. "Aprovação" endereça por id de `Member`, porque é o membro que a decisão de
 * aprovação registra. Reaproveitar uma lista na outra chave devolve resultado errado **sem erro
 * nenhum**, e foi por isso que as duas fontes ficaram separadas aqui.
 */
export function useDocumentFilterCollaborators(
  elaborationPermission: CollaboratorPermissionCheck,
  includeApprovalMembers = false
) {
  const { users: elaborationMembers, fetchUsers: fetchElaborationUsers } = usePermittedMembers({
    requiredPermissions: [elaborationPermission],
    includeInactiveMembers: true,
  });

  const { userOptions: approvalMemberOptions, fetchUsers: fetchApprovalUsers } = usePermittedMembers(
    {
      requiredPermissions: [{ action: 'manageApproval', subject: 'Homologation' }],
      includeInactiveMembers: true,
    }
  );

  // As duas varreduras são independentes e cada uma é paginada: em série elas dobram o tempo de
  // abertura do modal.
  const fetchCollaborators = async (): Promise<void> => {
    await Promise.all(
      includeApprovalMembers
        ? [fetchElaborationUsers(), fetchApprovalUsers()]
        : [fetchElaborationUsers()]
    );
  };

  const elaborationUsersList = computed<CollaboratorOption[]>(() =>
    (elaborationMembers.value?.data ?? []).map((user) => ({
      id: collaboratorUserId(user),
      name: optionLabel(user),
    }))
  );

  const approvalMembersList = computed<CollaboratorOption[]>(() =>
    approvalMemberOptions.value.map((member) => ({
      id: member.id,
      name: optionLabel(member),
    }))
  );

  return {
    fetchCollaborators,
    elaborationUsersList,
    approvalMembersList,
  };
}
